import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover';
import { cn } from '../../../lib/utils';
import type { BlogCategory } from '../../../types';

interface CategoryManagerProps {
  categories: BlogCategory[];
  onCreateCategory: (data: {
    name: string;
    description?: string;
    color?: string;
  }) => Promise<void>;
  onUpdateCategory: (
    id: string,
    data: { name?: string; description?: string; color?: string; sortOrder?: number }
  ) => Promise<void>;
  onDeleteCategory: (id: string) => Promise<void>;
}

interface EditState {
  name: string;
  description: string;
  color: string;
}

export function CategoryManager({
  categories,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
}: CategoryManagerProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ name: '', description: '', color: '#50E3C2' });
  const [isAdding, setIsAdding] = useState(false);
  const [newState, setNewState] = useState<EditState>({ name: '', description: '', color: '#50E3C2' });
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isSavingNew, setIsSavingNew] = useState(false);

  const startEdit = useCallback((cat: BlogCategory) => {
    setEditingId(cat.id);
    setEditState({ name: cat.name, description: cat.description ?? '', color: cat.color ?? '#50E3C2' });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    setSavingId(editingId);
    try {
      await onUpdateCategory(editingId, {
        name: editState.name,
        description: editState.description,
        color: editState.color,
      });
      setEditingId(null);
    } finally {
      setSavingId(null);
    }
  }, [editingId, editState, onUpdateCategory]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await onDeleteCategory(id);
    } finally {
      setDeletingId(null);
    }
  }, [onDeleteCategory]);

  const handleCreate = useCallback(async () => {
    if (!newState.name.trim()) return;
    setIsSavingNew(true);
    try {
      await onCreateCategory({
        name: newState.name.trim(),
        description: newState.description || undefined,
        color: newState.color,
      });
      setNewState({ name: '', description: '', color: '#50E3C2' });
      setIsAdding(false);
    } finally {
      setIsSavingNew(false);
    }
  }, [newState, onCreateCategory]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Categorias</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
        >
          <Plus className="w-4 h-4 mr-1" />
          Nova Categoria
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">Cor</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead className="w-20 text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* New row */}
          {isAdding && (
            <TableRow>
              <TableCell>
                <ColorPickerCell
                  color={newState.color}
                  onChange={(c) => setNewState((s) => ({ ...s, color: c }))}
                />
              </TableCell>
              <TableCell>
                <Input
                  value={newState.name}
                  onChange={(e) => setNewState((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Nome"
                  className="h-7 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                    if (e.key === 'Escape') setIsAdding(false);
                  }}
                />
              </TableCell>
              <TableCell>
                <span className="text-xs text-muted-foreground">
                  {slugify(newState.name)}
                </span>
              </TableCell>
              <TableCell>
                <Input
                  value={newState.description}
                  onChange={(e) => setNewState((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Descrição (opcional)"
                  className="h-7 text-sm"
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={handleCreate}
                    disabled={isSavingNew || !newState.name.trim()}
                  >
                    <Check className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => setIsAdding(false)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )}

          {/* Existing rows */}
          {categories.map((cat) => {
            const isEditing = editingId === cat.id;
            const isSaving = savingId === cat.id;
            const isDeleting = deletingId === cat.id;

            return (
              <TableRow key={cat.id}>
                <TableCell>
                  {isEditing ? (
                    <ColorPickerCell
                      color={editState.color}
                      onChange={(c) => setEditState((s) => ({ ...s, color: c }))}
                    />
                  ) : (
                    <span
                      className="inline-block w-4 h-4 rounded-full border border-border"
                      style={{ backgroundColor: cat.color ?? '#50E3C2' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <Input
                      value={editState.name}
                      onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))}
                      className="h-7 text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                  ) : (
                    <span className="font-medium text-sm">{cat.name}</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground font-mono">
                    {cat.slug}
                  </span>
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <Input
                      value={editState.description}
                      onChange={(e) => setEditState((s) => ({ ...s, description: e.target.value }))}
                      className="h-7 text-sm"
                      placeholder="Descrição (opcional)"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {cat.description || '-'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {isEditing ? (
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={saveEdit}
                        disabled={isSaving}
                      >
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={cancelEdit}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => startEdit(cat)}
                        disabled={isDeleting}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={cn(
                          'h-7 w-7 p-0',
                          'hover:text-destructive hover:bg-destructive/10'
                        )}
                        onClick={() => handleDelete(cat.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {categories.length === 0 && !isAdding && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">
                Nenhuma categoria cadastrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ColorPickerCell({
  color,
  onChange,
}: {
  color: string;
  onChange: (c: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-block w-6 h-6 rounded-full border border-border cursor-pointer hover:ring-2 hover:ring-ring"
          style={{ backgroundColor: color }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <HexColorPicker color={color} onChange={onChange} />
        <Input
          value={color}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 h-7 text-xs font-mono"
          maxLength={7}
        />
      </PopoverContent>
    </Popover>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}
